// The signing review panel (docs/08-app-design.md 5.5 step 2, "the trust
// centre of the app"; design report 2026-09-29 section 8), shared by Swap
// and Withdraw: a titled review ("Review your swap"), the bounds as rows, the
// largest figure being what the vault keeps, the network fee and any account
// funding as separate lines paid from the fee reserve (R49; its account
// minimum F and the key-refresh hold K named apart, docs/18 5.1 A), the expiry, the
// closing sentence, and the signing details (which key) one tap away. Nothing is signed while it is open;
// the caller's confirm button lives inside it so the two never drift apart,
// and it is disabled while the fee reserve cannot pay. The outcome line names
// one of the four outcomes of engine.ts (R45): landed, not sent, failed on
// chain, outcome unknown.
// Round 4 (brief section 8): the net figure stays the strongest; the rows sit
// in small labelled groups (what you spend and get, costs, route and
// validity), each still a dl.kv of dt/dd rows; a quoted action shows the
// quote's age until Confirm, separate from the expiry that starts at
// Confirm; and on phones the one actions area (the same Confirm and Back,
// never a second Confirm) stays in view above the bottom bar with the net
// figure (and the spend bound, when the caller gives one) repeated beside it;
// once Confirm is pressed, each new outcome line is scrolled into view above
// that bar.
// Under "Fee reserve too low" the panel points to the move when this device's
// older fee reserve can cover it (docs/18-one-fee-reserve.md section 6: "the
// panel points to the move"): the refusal's own reading
// (InsufficientGas.movable) when the caller has one, else one read of its own.

import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import type { ActionProgress, CostEstimate, Engine, OlderReserve } from '../engine';
import { explorerBase, explorerUrl, shortSig } from '../util';
import { OlderReservePointer, movableOf } from './OlderReserve';
import { Amount, Button, Countdown, StatusLine } from './primitives';

export interface ReviewRow {
  label: string;
  value: ReactNode;
  /** A second line under the value, muted. */
  detail?: ReactNode;
  /** The labelled group the row sits in (REVIEW_GROUPS); rows without one open the review, unlabelled. */
  group?: string;
  /** The value takes the row's full width under its label: for a full address, read left to right. */
  wide?: boolean;
}

/**
 * The review's row groups, in order. The panel adds its own rows to them:
 * the network fee and any account funding to Costs, the expiry to Route and
 * validity (or to Validity when the caller has no route row).
 */
export const REVIEW_GROUPS = { terms: 'What you spend and get', costs: 'Costs', route: 'Route and validity' } as const;

/** How long ago a quote was fetched, ticking each second; a different thing from the signed transaction's expiry. */
function QuoteAge({ at }: { at: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [at]);
  // A timestamp that is not a time says nothing about the quote's age: no line, rather than a wrong one or a thrown RangeError.
  if (!Number.isFinite(at)) return null;
  const s = Math.max(0, Math.floor((now - at) / 1000));
  const text = s < 2 ? 'just now' : s < 60 ? `${s} s ago` : `${Math.floor(s / 60)} min ${s % 60} s ago`;
  return <time dateTime={new Date(at).toISOString()}>Quoted {text}</time>;
}

export const STEP_WORDS: Record<ActionProgress['step'], string> = { signing: 'Signing.', checking: 'Checking.', sending: 'Sending.', landed: 'Landed.', rejected: 'Not sent.', failed: 'Failed on chain.', unknown: 'Outcome unknown.', expired: 'Expired.' };

/** The status line's tone for each step: only `landed` and `failed` are chain facts; `rejected` never reached the chain; `expired` is a retirement without a confirmation; `unknown` is none of these yet. */
export function stepState(step: ActionProgress['step']): 'landed' | 'pending' | 'failed' | 'neutral' {
  if (step === 'landed') return 'landed';
  if (step === 'rejected' || step === 'failed' || step === 'expired') return 'failed';
  if (step === 'unknown') return 'neutral';
  return 'pending';
}

export function isBusy(progress: ActionProgress | null): boolean {
  return progress ? progress.step === 'signing' || progress.step === 'checking' || progress.step === 'sending' : false;
}

/** Each account the transaction creates, with its rent. */
function fundingDetail(cost: CostEstimate): ReactNode {
  return (
    <>
      {cost.rent.map((r, i) => (
        <span key={r.label}>
          {i ? '; ' : ''}rent <Amount raw={r.lamports} decimals={9} symbol="SOL" maxFraction={9} /> for {r.label} ({r.bytes} bytes)
        </span>
      ))}
      . Paid once, from your fee reserve.
    </>
  );
}

export function ReviewPanel({
  engine,
  title,
  rows,
  net,
  bound,
  keyLine,
  rotates,
  cost,
  progress,
  rpcUrl,
  confirmLabel,
  onConfirm,
  onCancel,
  onRecheck,
  movable,
  disabled,
  updated,
  quote,
  children,
}: {
  engine: Engine;
  /** The panel's heading and accessible name, e.g. "Review your swap". */
  title: string;
  rows: ReviewRow[];
  /** The one figure a screen is about: label and value, shown largest and inverted. */
  net?: { label: string; value: ReactNode; detail?: ReactNode };
  /**
   * The other side's bound (a swap's "Spend at most"), repeated under the net figure in the phone's sticky actions so
   * both bounds are in view at Confirm. Like the repeated net figure it is aria-hidden and holds a second `.amount`:
   * locators inside the panel must stay scoped (`dd`, `.swap-amount.net`, `.detail`), never a bare `.amount`.
   */
  bound?: { label: string; value: ReactNode };
  keyLine: string;
  /** True when this signature also installs the next key tree (the signer's rotation policy, R43). */
  rotates?: boolean;
  /** What the action costs the fee reserve; null while it is being estimated. Confirm is disabled until it is known and affordable (R49). */
  cost: CostEstimate | null;
  progress: ActionProgress | null;
  rpcUrl: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel?: () => void;
  /** Re-estimate the cost after a top-up of the fee reserve; offered while the reserve is too low. */
  onRecheck?: () => void;
  /**
   * When `cost` came from an `InsufficientGas` refusal: its `movable`, this device's older reserve as the engine read it
   * at the refusal (null when it cannot be moved). Undefined otherwise: the panel then reads the older reserve once
   * itself while the reserve is too low.
   */
  movable?: OlderReserve | null;
  disabled?: boolean;
  /** True right after a quote refresh: the panel says "Quote updated". */
  updated?: boolean;
  /** A quoted action: when the quote was fetched, and one line on when it changes. Shown until Confirm. */
  quote?: { at: number; note: ReactNode };
  children?: ReactNode;
}) {
  const busy = isBusy(progress);
  const done = progress?.step === 'landed' || progress?.step === 'unknown' || progress?.step === 'expired';
  const clock = engine.clock.latest();
  const txBase = explorerBase(rpcUrl, 'tx');
  const unaffordable = cost !== null && !cost.enough;
  const headingId = useId();
  const rentTotal = cost ? cost.rent.reduce((sum, r) => sum + r.lamports, 0n) : 0n;
  const ids = { fee: `${headingId}-fee`, low: `${headingId}-low`, progress: `${headingId}-progress` };

  // The older reserve the "too low" line points to (docs/18 section 6). An estimate that comes back too low leaves
  // Confirm disabled, so no refusal carries a reading: the panel reads once (and again after Check again), and a
  // refusal's own reading, when there is one, wins. A failed read, a lock included, shows no pointer.
  const [olderRead, setOlderRead] = useState<OlderReserve | null>(null);
  const olderAsked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!unaffordable || movable !== undefined || olderAsked.current) return;
    olderAsked.current = true;
    engine.olderReserve().then(
      (reading) => {
        if (mounted.current) setOlderRead(movableOf(reading));
      },
      () => undefined,
    );
  }, [unaffordable, movable, engine]);
  const pointTo = movable !== undefined ? movable : olderRead;

  // Once Confirm has been pressed here, each new line under the review (an outcome, a refreshed quote, a notice) is
  // brought into view on phones, where the sticky actions could otherwise cover it. Nothing scrolls before that.
  const sectionRef = useRef<HTMLElement>(null);
  const armed = useRef(false);
  const seen = useRef('');
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || !armed.current) return;
    const lines = el.querySelectorAll<HTMLElement>(':scope > .status-line, :scope > .notice');
    const sig = `${progress?.step ?? ''}:${lines.length}`;
    if (sig === seen.current) return;
    seen.current = sig;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 800px)').matches) lines[lines.length - 1]?.scrollIntoView({ block: 'nearest' });
  });

  // The rows in their labelled groups: the caller's rows first, in their own order; then the panel's own costs and expiry.
  const groups: Array<{ label: string | null; items: ReactNode[] }> = [];
  const groupFor = (label: string | null) => {
    let g = groups.find((x) => x.label === label);
    if (!g) {
      g = { label, items: [] };
      groups.push(g);
    }
    return g;
  };
  for (const r of rows) {
    groupFor(r.group ?? null).items.push(
      <div key={r.label} className={r.wide ? 'wide' : undefined}>
        <dt>{r.label}</dt>
        <dd>
          {r.value}
          {r.detail ? <small className="detail">{r.detail}</small> : null}
        </dd>
      </div>,
    );
  }
  const costs = groupFor(REVIEW_GROUPS.costs);
  costs.items.push(
    <div key="network-fee">
      <dt>Network fee</dt>
      <dd id={ids.fee}>
        {cost ? (
          <>
            <Amount raw={cost.fee} decimals={9} symbol="SOL" maxFraction={9} />
            <small className="detail">
              Paid from your fee reserve, which must hold at least <Amount raw={cost.total} decimals={9} symbol="SOL" maxFraction={9} /> for this action: the fee and any account funding below, <Amount raw={cost.reserve} decimals={9} symbol="SOL" maxFraction={9} /> held for a key refresh, and <Amount raw={cost.minimum} decimals={9} symbol="SOL" maxFraction={9} />, its account minimum, returned when the reserve is emptied.
            </small>
          </>
        ) : (
          'estimating…'
        )}
      </dd>
    </div>,
  );
  if (cost && cost.rent.length) {
    costs.items.push(
      <div key="account-funding">
        <dt>Account funding</dt>
        <dd>
          <Amount raw={rentTotal} decimals={9} symbol="SOL" maxFraction={9} />
          <small className="detail">{fundingDetail(cost)}</small>
        </dd>
      </div>,
    );
  }
  (groups.find((g) => g.label === REVIEW_GROUPS.route) ?? groupFor('Validity')).items.push(
    <div key="expires">
      <dt>Expires</dt>
      <dd>{progress?.validUntilSlot !== undefined && clock ? <Countdown validUntilSlot={progress.validUntilSlot} observedSlot={BigInt(clock.slot)} observedAt={clock.at} msPerSlot={engine.clock.msPerSlot()} /> : 'about 60 s after you confirm'}</dd>
    </div>,
  );
  const showAge = !!quote && (!progress || (progress.step === 'rejected' && !disabled));
  const confirmDisabled = busy || !!disabled || cost === null || unaffordable;
  // What the Confirm button's state depends on, for assistive technology: the outcome line, the fee reserve line, or the fee still being estimated.
  const confirmReason = progress ? ids.progress : unaffordable ? ids.low : cost === null ? ids.fee : undefined;

  return (
    <section className="review" aria-labelledby={headingId} ref={sectionRef}>
      <div className="review-top">
        <h2 id={headingId}>{title}</h2>
        {updated ? <span className="tag accent">Quote updated</span> : null}
      </div>
      {showAge ? (
        <p className="review-age">
          <QuoteAge at={quote!.at} />
          <span>{quote!.note}</span>
        </p>
      ) : null}
      {net ? (
        <div className="swap-amount net">
          <span>{net.label}</span>
          <strong>{net.value}</strong>
          {net.detail ? <small>{net.detail}</small> : null}
        </div>
      ) : null}
      <div className="review-groups">
        {groups.map((g, i) => (
          <div className="review-group" key={g.label ?? 'main'}>
            {g.label ? (
              <h3 className="review-group-label" id={`${headingId}-g${i}`}>
                {g.label}
              </h3>
            ) : null}
            <dl className="kv">{g.items}</dl>
          </div>
        ))}
      </div>
      <p className="review-bounds">Nothing else can be authorised by this signature.</p>
      <details className="signing" open={rotates || undefined}>
        <summary>Signing details</summary>
        <dl className="kv">
          <div>
            <dt>Key</dt>
            <dd>
              {progress?.leaf !== undefined ? `key ${progress.leaf} of 256, ${keyLine}` : `one of the unused keys of ${keyLine}`}
              {rotates ? <small className="detail">This signature also installs the next key tree: the vault refreshes its key as part of this action.</small> : null}
            </dd>
          </div>
        </dl>
      </details>
      {unaffordable && !progress ? (
        <>
          <StatusLine state="failed" word="Fee reserve too low." id={ids.low}>
            It holds <Amount raw={cost.tank} decimals={9} symbol="SOL" maxFraction={9} /> and this action needs at least <Amount raw={cost.total} decimals={9} symbol="SOL" maxFraction={9} />, its account minimum included, so nothing is signed. Top it up on the Deposit page, then check again.
          </StatusLine>
          {pointTo ? <OlderReservePointer reading={pointTo} /> : null}
          {onRecheck ? (
            <div className="actions">
              <Button
                variant="ghost"
                small
                onClick={() => {
                  // the older reserve is read again with the new estimate
                  olderAsked.current = false;
                  onRecheck();
                }}
              >
                Check again
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
      {children}
      {progress ? (
        <StatusLine state={stepState(progress.step)} word={STEP_WORDS[progress.step]} id={ids.progress}>
          {progress.step === 'rejected' || progress.step === 'failed' || progress.step === 'unknown' || progress.step === 'expired'
            ? progress.error
            : progress.step === 'landed' && progress.signature
              ? (
                <>
                  {explorerUrl(txBase, progress.signature) ? (
                    <>
                      Transaction{' '}
                      <a href={explorerUrl(txBase, progress.signature)!} target="_blank" rel="noreferrer">
                        {shortSig(progress.signature)} ↗
                      </a>
                      .
                    </>
                  ) : (
                    `Transaction ${shortSig(progress.signature)}.`
                  )}
                  {progress.note ? ` ${progress.note}` : ''}
                </>
              )
              : (progress.note ?? (progress.step === 'signing' ? 'Reserving a one-time key and signing inside this device.' : progress.step === 'checking' ? 'Simulating the exact transaction before it is sent.' : 'Sent; waiting for the network to confirm it.'))}
        </StatusLine>
      ) : null}
      {!done ? (
        <div className="review-actions">
          {net ? (
            <div className="review-actions-sum" aria-hidden="true">
              <p>
                <span>{net.label}</span>
                <strong>{net.value}</strong>
              </p>
              {bound ? (
                <p className="bound">
                  <span>{bound.label}</span>
                  <span>{bound.value}</span>
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="actions">
            <Button
              onClick={() => {
                armed.current = true;
                onConfirm();
              }}
              disabled={confirmDisabled}
              working={busy}
              aria-describedby={confirmReason}
            >
              {busy ? STEP_WORDS[progress!.step].replace('.', '') : confirmLabel}
            </Button>
            {onCancel ? (
              <Button variant="ghost" onClick={onCancel} disabled={busy}>
                Back
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
